import React from 'react';
import {
  initialStepsState,
  StepsActions,
  stepsReducer,
} from './use-steps.state';

interface UseStepsParams<T> {
  startStep: T;
  initialSteps: T[];
  initialCompleted?: Record<string, boolean>;
  reducer?: typeof stepsReducer<T>;
}

export const useSteps = <T = number | string,>({
  startStep,
  initialSteps = [],
  initialCompleted,
  reducer = stepsReducer as typeof stepsReducer<T>,
}: UseStepsParams<T>) => {
  const initialStepIndex = initialSteps.indexOf(startStep);
  const [
    {
      step,
      stepIndex,
      steps,
      completed,
      previousStep,
      nextStep,
      isFirstStep,
      isLastStep,
    },
    dispatch,
  ] = React.useReducer(reducer, {
    ...initialStepsState,
    step: initialSteps[initialStepIndex] || null,
    stepIndex: initialStepIndex,
    steps: initialSteps,
    completed: initialCompleted ?? {},
    nextStep: initialSteps[initialStepIndex + 1] || null,
    previousStep: initialSteps[initialStepIndex - 1] || null,
    isFirstStep: initialStepIndex === 0,
    isLastStep: initialStepIndex === initialSteps.length - 1,
  });

  const isCompleted = (stepToFind: string) => {
    return completed[stepToFind];
  };

  function goBack() {
    dispatch({ type: StepsActions.GO_BACK });
  }

  function goForward() {
    dispatch({ type: StepsActions.GO_FORWARD });
  }

  function completeStep(stepToComplete: T) {
    if (stepToComplete) {
      dispatch({ type: StepsActions.COMPLETE_STEP, payload: stepToComplete });
    } else {
      dispatch({ type: StepsActions.COMPLETE_STEP });
    }
  }

  function setSteps(nextSteps: T[] = []) {
    dispatch({ type: StepsActions.SET_STEPS, payload: nextSteps });
  }

  const jumpTo = React.useCallback((futureStep: T) => {
    dispatch({ type: StepsActions.JUMP_TO, payload: futureStep });
  }, []);

  return {
    step,
    stepIndex,
    stepNumber: stepIndex + 1,
    steps,
    setSteps,
    completed,
    isCompleted,
    nextStep,
    goForward,
    goBack,
    previousStep,
    completeStep,
    jumpTo,
    isLastStep,
    isFirstStep,
  };
};
